/**
 * Schlüsselkonvention für react-query in m1member.
 *
 * Ein Query-Key ist der Name, unter dem eine Antwort im Cache liegt. Zwei Anfragen mit demselben
 * Key gelten als dieselbe Sache — deshalb muss **alles, was das Ergebnis verändert**, im Key
 * stehen. In m1member ist das vor allem die Studio-Auswahl aus dem Kopfbereich: dieselbe URL
 * liefert je nach Auswahl andere Zeilen.
 *
 * Ohne den Scope im Key zeigt ein Studiowechsel den Cache-Stand des vorigen Studios — der
 * wahrscheinlichste Fehler bei der Umstellung. Deshalb nimmt {@link scopedKey} ihn verpflichtend
 * entgegen, statt ihn optional anzubieten.
 *
 * Keine losen Schlüssel-Literale in Seiten: `invalidateQueries` wird sonst zum Ratespiel.
 */

/** Bereich, unter dem ein Key liegt. Entspricht grob einer fachlichen Fläche. */
export type QueryArea =
  | "check-ins"
  | "open-items"
  | "contributions"
  | "payments"
  | "sepa-runs"
  | "reminders"
  | "leads"
  | "appointments"
  | "persons"
  | "documents"
  | "communication"
  | "reports"
  | "tasks"
  | "lookups";

/**
 * Kennung der aktuellen Studio-Auswahl für den Query-Key.
 *
 * Bewusst der bereits vorhandene `studioIdsParam` aus `useGlobalStudioContext`: leerer String
 * bedeutet „alle erlaubten Studios", sonst die kommagetrennten IDs. Damit trägt der Key genau die
 * Angabe, die auch in die Anfrage geht — Anzeige und Cache können nicht auseinanderlaufen.
 */
export type StudioScopeKey = string;

/**
 * Baut einen Query-Key aus Bereich, Studio-Auswahl und weiteren Bestandteilen.
 *
 * ```ts
 * const { studioIdsParam } = useGlobalStudioContext();
 * useQuery({ queryKey: scopedKey("open-items", studioIdsParam, { personId }), … });
 * ```
 */
export function scopedKey(
  area: QueryArea,
  studioScope: StudioScopeKey,
  ...parts: ReadonlyArray<string | number | boolean | null | undefined | Record<string, unknown>>
): readonly unknown[] {
  return [area, { studioScope }, ...parts.filter((p) => p !== undefined)] as const;
}

/**
 * Key für Daten, die nicht von der Studio-Auswahl abhängen (Stammlisten, Kataloge).
 *
 * Nur verwenden, wenn die Antwort nachweislich für alle Studios dieselbe ist — im Zweifel
 * {@link scopedKey}, denn ein zu feiner Key kostet nur einen Abruf, ein zu grober zeigt falsche
 * Daten.
 */
export function globalKey(
  area: QueryArea,
  ...parts: ReadonlyArray<string | number | boolean | null | undefined | Record<string, unknown>>
): readonly unknown[] {
  return [area, ...parts.filter((p) => p !== undefined)] as const;
}

/** Alle Keys eines Bereichs — für `invalidateQueries` nach einem Realtime-Ereignis. */
export function areaKey(area: QueryArea): readonly unknown[] {
  return [area] as const;
}
